import { useEffect, useState } from 'react'
import styles from './app.module.css'
import numeral from 'numeral'
import { Card, Cards, Mods, PointsToWin } from './types'
import { CalculateSum } from './utils/calc'
import Dexie, { EntityTable } from 'dexie'
import { useLiveQuery } from 'dexie-react-hooks'
import { DeserialiseCards, SerialiseCards } from './utils/serialise'

type Game = { id: number; created_at: string; cards: string[] }

// the db will only ever have one game available...
// to help store the state between browser refreshes
const db = new Dexie('flip7.app') as Dexie & {
  games: EntityTable<Game, 'id'>
}
db.version(1).stores({
  games: '++id,created_at,*cards',
})

const AbsoluteGameId = 1

export default function App() {
  const cards =
    useLiveQuery(async () => {
      let game = await db.games.get(AbsoluteGameId)
      if (!game) {
        // create a new entry and return it
        game = {
          id: AbsoluteGameId,
          created_at: new Date().toISOString(),
          cards: [],
        }
      }

      // ingest the cards from the db into a format the app will understand
      return DeserialiseCards(game.cards)
    }) ?? []

  async function CheckCreateInitialGame() {
    // check if the initial game exists with the id
    // if not, create one!
    if ((await db.games.get(AbsoluteGameId)) === undefined) {
      await db.games.put({
        id: AbsoluteGameId,
        created_at: new Date().toISOString(),
        cards: [],
      })
    }
  }

  useEffect(() => {
    CheckCreateInitialGame()
  }, [])

  // const [cards, SetCards] = useState<Card[][]>([])
  const [hand, SetHand] = useState<Card[]>([])

  const [shouldShowMods, ShowMods] = useState(false)
  const [showResetPrompt, ShowResetPrompt] = useState(false)

  function onCardClick(card: Card) {
    SetHand((hand) =>
      hand.includes(card) ? hand.filter((c) => c !== card) : [...hand, card],
    )
  }

  async function bank() {
    // save the hand to the db
    await db.games.update(AbsoluteGameId, {
      cards: SerialiseCards([...cards, hand]),
    })
    SetHand([])
  }

  async function reset() {
    await db.games.update(AbsoluteGameId, { cards: [] })
    SetHand([])
    ShowResetPrompt(false)
  }

  function onModsClick() {
    ShowMods((state) => !state)
  }

  function onModsAccept() {
    ShowMods((state) => !state)
  }

  const numCardsInHand = hand.filter((card) => !isNaN(Number(card))).length

  // render the cards
  const renderCards = Cards.map((card) => {
    const isInHand = hand.includes(card)

    const disabled = numCardsInHand >= 7 && !isInHand

    return (
      <div
        key={card}
        className={[styles.card, isInHand ? styles.hand : ''].join(' ').trim()}
        data-disabled={disabled}
        onClick={!disabled ? onCardClick.bind(null, card) : undefined}
      >
        <img src={`/cards/${card}.webp`} />
      </div>
    )
  })

  const renderMods = Mods.map((mod) => {
    const isInHand = hand.includes(mod)
    // const isInDeck = cards.find((cards) => cards.includes(mod)) !== undefined

    return (
      <div
        key={mod}
        className={[styles.card, isInHand ? styles.hand : ''].join(' ').trim()}
        // data-disabled={isInDeck}
        onClick={onCardClick.bind(null, mod)}
      >
        {/* <div> */}
        <img src={`/cards/${mod}.webp`} />
        {/* <div className={styles.cardCount}>{instances}</div> */}
        {/* </div> */}
      </div>
    )
  })

  const sumOfHand = CalculateSum(hand) + (numCardsInHand === 7 ? 15 : 0)
  const score = cards.reduce((total, cards) => {
    const raw = total + CalculateSum(cards)
    const numCards = cards.filter((card) => !isNaN(Number(card)))

    // add bonus points for flip 7 (7 cards)
    if (numCards.length === 7) {
      return raw + 15
    }

    return raw
  }, 0)

  const renderScore = numeral(score)
    .format('000')
    .split('')
    .map((i, index) => <span key={index}>{i}</span>)

  return (
    <div className={styles.wrapper}>
      <div className={styles.headerWrapper}>
        <div className={styles.header}>
          <div
            className={[styles.boxWithCornerBorders, styles.score].join(' ')}
          >
            {renderScore}
            {hand.length > 0 && (
              <span className={styles.toBank}>+{sumOfHand}</span>
            )}
            {numCardsInHand === 7 && (
              <div className={styles.bonus}>
                !&nbsp;b&nbsp;o&nbsp;n&nbsp;u&nbsp;s&nbsp;&nbsp;a&nbsp;d&nbsp;d&nbsp;e&nbsp;d&nbsp;!
              </div>
            )}
          </div>
          <div
            className={[
              styles.boxWithCornerBorders,
              styles.remainingScore,
            ].join(' ')}
          >
            <div className={styles.box}>
              {PointsToWin - score < 0 ? 0 : PointsToWin - score}
            </div>
            <span>to win</span>
          </div>
        </div>
      </div>
      {/* <hr /> */}

      <div className={styles.cardWrapper}>{renderCards}</div>

      <div className={styles.btnWrapper}>
        <button
          type="button"
          className={[styles.btn].join(' ')}
          onClick={ShowResetPrompt.bind(null, true)}
        >
          Reset
        </button>
        <button
          type="button"
          className={[styles.btn, styles.bank].join(' ')}
          onClick={hand.length > 0 ? bank : undefined}
          disabled={hand.length <= 0}
        >
          Bank
        </button>
        <button
          type="button"
          className={[styles.btn].join(' ')}
          onClick={onModsClick}
        >
          <div>Mods</div>
          {/* <div className={styles.cardCount}>0</div> */}
        </button>
      </div>

      {shouldShowMods && (
        <div className={styles.mods}>
          <div>
            <div>
              <div className={[styles.cardWrapper].join(' ')}>{renderMods}</div>
              <button
                type="button"
                className={styles.acceptMods}
                onClick={onModsAccept}
              >
                Accept
              </button>
            </div>
          </div>
        </div>
      )}

      {score >= 200 && (
        <div className={styles.notice} onClick={reset}>
          <div>
            <div>
              <h1>you won</h1>
              <br />
              <sub>tap to play again</sub>
            </div>
          </div>
        </div>
      )}

      {showResetPrompt && (
        <div className={styles.notice}>
          <div>
            <div>
              <h1>reset?</h1>
              <sub>this will reset all progress of this current game</sub>
              <div className={styles.resetOptsBtnWrapper}>
                <button
                  type="button"
                  className={styles.btn}
                  onClick={ShowResetPrompt.bind(null, false)}
                >
                  no
                </button>
                <button type="button" className={styles.btn} onClick={reset}>
                  yes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
